import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import CustomControl from '../CustomControl';
import InfoCircleIcon from '../../icons/InfoCircleIcon';

const propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['radio', 'checkbox']).isRequired,
  label: PropTypes.node.isRequired,
  className: PropTypes.string,
  description: PropTypes.node,
  children: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  boxStyle: PropTypes.oneOf([
    'primary',
    'secondary',
    'primary-blue',
    'outlined',
    'floating',
  ]),
  boxSize: PropTypes.oneOf(['sm', 'md']),
  labelTip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelTipPlace: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  customControlProps: PropTypes.object,
  disabledTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

const BoxedCustomControl = ({
  id,
  type,
  className,
  label,
  description,
  children,
  checked,
  onChange,
  onBlur,
  disabled,
  icon,
  boxStyle,
  boxSize = 'md',
  labelTip,
  labelTipPlace,
  customControlProps = {},
  disabledTooltip,
}) => {
  const classes = classNames({
    'boxed-custom-control': true,
    'boxed-custom-control-checked': checked,
    'boxed-custom-control-disabled': disabled,
    [`boxed-custom-control-${boxStyle}`]: !!boxStyle,
    [`boxed-custom-control-${boxSize}`]: !!boxSize,
    [className]: !!className,
  });

  const LabelTip = () => (
    <span
      className="label-tip"
      data-for={`tooltip-${id}`}
      data-tip=""
      data-event="mouseenter click"
      data-effect="solid"
      data-place={labelTipPlace}
    >
      <InfoCircleIcon size={15} />

      <ReactTooltip
        id={`tooltip-${id}`}
        className="tooltip"
        globalEventOff="click"
      >
        {labelTip}
      </ReactTooltip>
    </span>
  );

  return (
    <>
      <div
        className={classes}
        onClick={disabled ? undefined : onChange}
        onBlur={disabled ? undefined : onBlur}
        role="button"
        tabIndex={0}
        data-for={`disabled-tooltip-${id}`}
        data-tip=""
      >
        <div className="boxed-custom-control-input">
          <CustomControl
            id={id}
            type={type}
            label={label}
            labelSrOnly
            checked={checked}
            readOnly
            {...customControlProps}
          />
        </div>

        <div className="boxed-custom-control-content">
          {icon && <span className="boxed-custom-control-icon">{icon}</span>}

          <div className="boxed-custom-control-label">
            {label}
            {labelTip && <LabelTip />}
          </div>

          {description && (
            <div className="boxed-custom-control-description">
              {description}
            </div>
          )}

          {children && (
            <div className="boxed-custom-control-children">{children}</div>
          )}
        </div>
      </div>
      {disabled && disabledTooltip && (
        <ReactTooltip
          id={`disabled-tooltip-${id}`}
          className="tooltip"
          globalEventOff="click"
        >
          {disabledTooltip}
        </ReactTooltip>
      )}
    </>
  );
};

BoxedCustomControl.propTypes = propTypes;

export default BoxedCustomControl;
